import { Inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { SNAPSHOT_REPORT_REFRESH_SKU, SNAPSHOT_REPORT_SKU } from '@galaxy/billing';
import { Environment, EnvironmentService } from '@galaxy/core';
import { WhitelabelService } from '@galaxy/partner';
import { WhitelabelTranslationService } from '@galaxy/snapshot';
import { DomainService } from '@vendasta/domain';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { RetryConfig, retryer } from '@vendasta/rx-utils';
import { ObservableWorkStateMap } from '@vendasta/rx-utils/work-state';
import {
  CurrentSnapshotService,
  GetCurrentResponseInterface,
  Origin,
  SnapshotService as SnapshotApiService,
} from '@vendasta/snapshot';
import { combineLatest, interval, Observable, of, throwError } from 'rxjs';
import { distinctUntilChanged, filter, map, retry, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { ApiService } from '../api-service/api.service';
import { CheckoutDialogComponent } from '../checkout/checkout-dialog.component';
import { Feature } from './access';

export interface SnapshotUrl {
  view: string;
  edit: string;
}

export interface CurrentSnapshot {
  snapshotID: string;
  created?: Date;
  expired?: Date;
  path?: string;
}
export interface SnapshotRefreshResponse {
  snapshotId?: string;
}

export interface CreateSnapshotRequest {
  partnerId: string;
  origin: string;
  accountGroupId: string;
}

export interface ConfirmSnapshotDialogRequest {
  accountGroupId: string;
  partnerId: string;
  action: SnapshotAction;
  accountType?: string;
  description?: string;
}

interface ConfirmSnapshotDialogResponse {
  create: boolean;
  inferMissingData: boolean;
  accountGroupId: string;
}

export enum SnapshotAction {
  CREATE = 'create',
  REFRESH = 'refresh',
}

export enum SnapshotCaller {
  COMPANY = 'company',
  ACCOUNT_GROUP = 'account',
}

@Injectable({ providedIn: 'root' })
export class SnapshotService {
  private readonly refreshSnapshotState = new ObservableWorkStateMap<string, SnapshotRefreshResponse>();
  private readonly currentSnapshotState = new ObservableWorkStateMap<string, CurrentSnapshot>();
  private readonly createSnapshotState = new ObservableWorkStateMap<string, SnapshotRefreshResponse>();
  private readonly snapshotNameState = new ObservableWorkStateMap<string, string>();

  constructor(
    private readonly apiService: ApiService,
    private readonly snapshotApiService: SnapshotApiService,
    private readonly currentSnapshotService: CurrentSnapshotService,
    private readonly dialog: MatDialog,
    private readonly whitelabelTranslationService: WhitelabelTranslationService,
    private readonly snackbarService: SnackbarService,
    private readonly whitelabelService: WhitelabelService,
    private readonly accountGroupService: AccountGroupService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private readonly domainService: DomainService,
    private readonly environmentService: EnvironmentService,
  ) {}

  private get host(): string {
    switch (this.environmentService.getEnvironment()) {
      case Environment.DEMO:
        return `https://salestool-demo.appspot.com`;
      case Environment.PROD:
        return `https://www.snapshotreport.biz`;
      default:
        return `https://www.snapshotreport.biz`;
    }
  }

  private getDomain(): Observable<string> {
    return this.partnerId$.pipe(
      switchMap((id) => this.domainService.getDomainByIdentifier(`/application/ST/partner/${id}`)),
      map((domainResp) => domainResp.primary),
      map((d) => {
        const scheme = d.secure ? 'https' : 'http';
        if (d.domain) {
          return `${scheme}://${d.domain}`;
        }
        return this.host;
      }),
    );
  }

  getSnapshotUrl$(snapshotId: string): Observable<SnapshotUrl> {
    const retryConfig: RetryConfig = {
      maxAttempts: 5,
      retryDelay: 100,
      timeoutMilliseconds: 10000,
    };

    const domain$ = this.getDomain();
    const snapshot$ = this.snapshotApiService.get(snapshotId).pipe(retryer(retryConfig));

    return combineLatest([domain$, snapshot$]).pipe(
      map(([domain, snapshot]) => {
        if (!snapshot.snapshotId) {
          const url: SnapshotUrl = { view: ``, edit: `` };

          return url;
        }

        const snapshotID = snapshot.snapshotId;

        const url: SnapshotUrl = {
          view: `${domain}/snapshot/${snapshotID}`,
          edit: `${domain}/snapshot/v2/${snapshotID}/edit`,
        };

        return url;
      }),
      take(1),
    );
  }

  fetchSnapshotName(accountGroupId?: string): void {
    const id = accountGroupId ?? '';
    const defaultSnapshotName = this.whitelabelTranslationService.instant('SNAPSHOT_REPORT.COMMON.SNAPSHOT_NAME');

    const filter = new ProjectionFilter({
      accountGroupExternalIdentifiers: true,
    });

    let work$ = this.partnerId$.pipe(
      switchMap((partnerId) => this.whitelabelService.getConfiguration(partnerId)),
      map((config) => config?.snapshotConfiguration?.snapshotReportName || defaultSnapshotName),
    );

    if (id !== '') {
      work$ = this.accountGroupService.get(id, filter).pipe(
        switchMap((acc) =>
          this.whitelabelService.getConfiguration(acc.externalIdentifiers.partnerId, acc.externalIdentifiers.marketId),
        ),
        map((config) => config?.snapshotConfiguration?.snapshotReportName || defaultSnapshotName),
      );
    }

    this.snapshotNameState.startWork(id, work$);
  }

  getSnapshotName$(accountGroupId: string): Observable<string> {
    return this.snapshotNameState.getWorkResults$(accountGroupId);
  }

  getCurrentSnapshotSuccess$(accountGroupID: string): Observable<boolean> {
    return this.currentSnapshotState.isSuccess$(accountGroupID);
  }
  currentSnapshotLoading$(accountGroupID: string): Observable<boolean> {
    return this.currentSnapshotState.isLoading$(accountGroupID).pipe(startWith(true), distinctUntilChanged());
  }

  createSnapshotSuccess$(accountGroupID: string): Observable<boolean> {
    return this.createSnapshotState.isSuccess$(accountGroupID);
  }

  createSnapshotLoading$(accountGroupID: string): Observable<boolean> {
    return this.createSnapshotState.isLoading$(accountGroupID);
  }

  createSnapshotResult$(accountGroupID: string): Observable<string | null> {
    return this.currentSnapshotLoading$(accountGroupID).pipe(
      filter((loading) => !loading),
      switchMap(() => this.createSnapshotSuccess$(accountGroupID)),
      switchMap((success) => (success ? this.createSnapshotState.getWorkResults$(accountGroupID) : of(null))),
      map((resp) => resp?.snapshotId),
    );
  }

  getCurrentSnapshotUpdates$(accountGroupID: string): Observable<CurrentSnapshot> {
    return this.currentSnapshotState.getWorkResults$(accountGroupID);
  }

  getCurrentSnapshotV2$(accountGroupID: string): Observable<CurrentSnapshot> {
    const work$ = this.getCurrentSnapshot$(accountGroupID);
    this.currentSnapshotState.startWork(accountGroupID, work$);
    return this.currentSnapshotState.getWorkResults$(accountGroupID);
  }

  getCurrentSnapshot$(accountGroupID: string, refreshedSnapshotID?: string): Observable<CurrentSnapshot> {
    return this.currentSnapshotService.get(accountGroupID).pipe(
      map((resp: GetCurrentResponseInterface) => {
        const currentSnapshot: CurrentSnapshot = {
          snapshotID: resp.snapshot?.snapshotId,
          created: resp.snapshot?.created,
          expired: resp.snapshot?.expired,
          path: resp.snapshot?.path,
        };

        return currentSnapshot;
      }),
      map((snapshot) => {
        if (refreshedSnapshotID && snapshot.snapshotID !== refreshedSnapshotID) {
          throw new Error('Current Snapshot does not equal the refreshed Snapshot ID');
        }
        return snapshot;
      }),
    );
  }

  snapshotRefreshLoading$(accountId: string): Observable<boolean> {
    return this.refreshSnapshotState.isLoading$(accountId);
  }

  snapshotRefreshIsSuccessful$(accountId: string): Observable<boolean> {
    return this.refreshSnapshotState.isSuccess$(accountId);
  }

  private generateSnapshot$(
    accountId: string,
    originDetails: string[],
    inferMissingData: boolean,
  ): Observable<SnapshotRefreshResponse> {
    const notifier$ = interval(2000);
    return this.snapshotApiService.provision(accountId, Origin.PARTNER_CENTER, originDetails, inferMissingData).pipe(
      map((id) => <SnapshotRefreshResponse>{ snapshotId: id }),
      tap((resp) =>
        this.currentSnapshotState.startWork(
          accountId,
          this.getCurrentSnapshot$(accountId, resp.snapshotId).pipe(retry({ delay: () => notifier$, count: 30 })),
        ),
      ),
    );
  }

  doRefreshSnapshotWork(accountId: string, originDetails: string[], inferMissingData: boolean): void {
    const work$ = this.generateSnapshot$(accountId, originDetails, inferMissingData);
    this.refreshSnapshotState.startWork(accountId, work$);
  }

  doCreateSnapshotWork(accountId: string, originDetails: string[], inferMissingData: boolean): void {
    const work$ = this.generateSnapshot$(accountId, originDetails, inferMissingData);
    this.createSnapshotState.startWork(accountId, work$);
  }

  /** @deprecated use doRefreshSnapshotWork */
  refreshSnapshot(accountId: string, originDetails: string[], inferMissingData: boolean): Observable<any> {
    return this.snapshotApiService.provision(accountId, Origin.PARTNER_CENTER, originDetails, inferMissingData);
  }

  getSnapshotReportUrl(accountId: string): Observable<string> {
    return this.apiService.get('/_ajax/v1/st/generate-snapshot-url/', {
      params: {
        accountGroupId: accountId,
      },
    });
  }

  createCompanySnapshot(opts: CreateSnapshotRequest): Observable<string | null> {
    const req = <ConfirmSnapshotDialogRequest>{
      accountGroupId: opts.accountGroupId,
      partnerId: opts.partnerId,
      action: SnapshotAction.CREATE,
      accountType: SnapshotCaller.COMPANY,
    };
    const confirm$ = this.openConfirmSnapshotCreateDialog(req);
    const startWork$ = confirm$.pipe(
      map((res) => {
        if (!res?.create) {
          return null;
        }
        this.doCreateSnapshotWork(res.accountGroupId, [opts.origin], res.inferMissingData);
        return res.accountGroupId;
      }),
    );
    const res$ = startWork$.pipe(
      take(1),
      switchMap((accountGroupId) => (accountGroupId ? this.createSnapshotResult$(accountGroupId) : of(null))),
    );
    return res$;
  }

  refreshCompanySnapshot(opts: CreateSnapshotRequest): Observable<boolean> {
    if (!opts.accountGroupId) {
      return throwError(() => 'No account group ID');
    }
    const req = <ConfirmSnapshotDialogRequest>{
      accountGroupId: opts.accountGroupId,
      partnerId: opts.partnerId,
      action: SnapshotAction.REFRESH,
    };
    const confirm$ = this.openConfirmSnapshotCreateDialog(req);

    const startWork$ = confirm$.pipe(
      map((res) => {
        if (!res?.create) {
          return null;
        }
        this.doRefreshSnapshotWork(res.accountGroupId, [opts.origin], res.inferMissingData);
        return res.accountGroupId;
      }),
    );
    const res$ = startWork$.pipe(
      take(1),
      switchMap((accountGroupId) => {
        return this.snapshotRefreshIsSuccessful$(accountGroupId);
      }),
    );
    return res$;
  }

  showCreateSnapshotErrorMessage(): void {
    this.snackbarService.openErrorSnack(
      this.whitelabelTranslationService.instant('SNAPSHOT_REPORT.ERROR.UNABLE_TO_CREATE'),
    );
  }

  showCreateSnapshotSuccessMessage(): void {
    this.snackbarService.openSuccessSnack(
      this.whitelabelTranslationService.instant('SNAPSHOT_REPORT.MESSAGE.GENERATING'),
    );
  }

  showRefreshSnapshotErrorMessage(): void {
    this.snackbarService.openErrorSnack(
      this.whitelabelTranslationService.instant('SNAPSHOT_REPORT.ERROR.UNABLE_TO_REFRESH'),
    );
  }

  showRefreshSnapshotSuccessMessage(): void {
    this.snackbarService.openSuccessSnack(
      this.whitelabelTranslationService.instant('SNAPSHOT_REPORT.MESSAGE.SUCCESSFULLY_REFRESHED'),
    );
  }

  openConfirmSnapshotCreateDialog(req: ConfirmSnapshotDialogRequest): Observable<ConfirmSnapshotDialogResponse> {
    this.dialog.closeAll();
    let title = 'SNAPSHOT_REPORT.ACTION.CREATE';
    let successLabel = 'SNAPSHOT_REPORT.COMMON.CREATE';
    let createButtonId = 'snapshotCreateButton';
    let successId = 'snapshotCreateButton';
    let sku = SNAPSHOT_REPORT_SKU;

    if (req.action === SnapshotAction.REFRESH) {
      title = 'SNAPSHOT_REPORT.ACTION.REFRESH';
      successLabel = 'SNAPSHOT_REPORT.COMMON.REFRESH';
      createButtonId = 'snapshotRefreshButton';
      successId = 'snapshotRefreshButton';
      sku = SNAPSHOT_REPORT_REFRESH_SKU;
    }

    const dialogRef: MatDialogRef<CheckoutDialogComponent> = this.dialog.open(CheckoutDialogComponent, {
      width: '480px',
      data: {
        partnerId: req.partnerId,
        title: this.whitelabelTranslationService.instant(title),
        successLabel: this.whitelabelTranslationService.instant(successLabel),
        productSKU: sku,
        createButtonId: createButtonId,
        successId: successId,
        accountGroupId: req.accountGroupId,
        accountType: req.accountType,
        description: req.description,
      },
    });
    return dialogRef.afterClosed().pipe(
      take(1),
      map(
        (result) =>
          ({
            create: result?.status === 'success',
            inferMissingData: result?.inferMissingData,
            accountGroupId: req.accountGroupId,
          }) as ConfirmSnapshotDialogResponse,
      ),
    );
  }

  partnerHasAccessToSnapshotEditing(): Observable<boolean> {
    return this.partnerId$.pipe(
      switchMap((partnerId) => this.whitelabelService.getConfiguration(partnerId)),
      map((config) => config.enabledFeatures),
      map((features) => features.includes(Feature.snapshotReportCustomization)),
      shareReplay(1),
    );
  }
}
